// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/WineSheet.tsx
// 🔧 PURPOSE: LA SCHEDA DI UNA BOTTIGLIA di "Vino & Prosecco": foto grande,
//             chi la fa, i dati dell'etichetta, la storia, profumi e
//             abbinamenti, e in fondo il bottone che aggiunge UNA bottiglia
//             alla selezione della card (la stessa cosa del "+" dello
//             stepper).
//
//             Tre aspetti, decisi dal channel manager (`info.kind`):
//              - il VINO (di serie, es. il Kōra): rosso vino, "Scopri il vino";
//              - lo SPRITZ (`kind: 'spritz'`): arancio, "Scopri lo spritz",
//                "Cos'è", "Come servirlo", "Il gusto", "Quando e con cosa";
//              - il PROSECCO (`kind: 'prosecco'`): la stessa scheda dello
//                spritz in oro chiaro delle bollicine, "Scopri il prosecco".
//             Il contenuto è lo stesso per tutti e tutto facoltativo;
//             alcuni dati nati con lo spritz (gradazione come intervallo,
//             bicchieri, "la marca può variare", avviso 18+ e solfiti, passi
//             per servirlo) si mostrano per qualsiasi bottiglia che li abbia.
//             Un tipo che l'app non conosce apre la scheda del vino.
//
//             I testi della bottiglia arrivano già tradotti dal channel
//             manager (`wineVariants[].info`): qui si mostra solo quello che
//             c'è. Le etichette fisse stanno in `extras.wine.*` e, per
//             spritz e prosecco, in `extras.spritz.*` ed `extras.prosecco.*`.
//
//             Stesso schema degli altri fogli dell'app (ExtrasPaySheet):
//             portal sul body, sfondo che chiude al tocco, scroll della
//             pagina bloccato sotto. In più: Esc chiude, il focus parte
//             dalla X e il Tab resta dentro la scheda; con "Riduci
//             movimento" la scheda compare senza salire.
//
//             La barra di stato, finché la scheda è aperta, prende il colore
//             del bottone "Aggiungi al soggiorno" (rosso vino, arancio
//             spritz o oro prosecco, lo stesso in chiaro e in scuro): sul
//             telefono la scheda copre tutto lo schermo.
// ============================================

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Grape, Info, MapPin, Sparkles, Sunset, UtensilsCrossed, Wine, X, type LucideIcon } from 'lucide-react';
import { guestAssetUrl, type WineInfo } from '../lib/guestApi';
import { wineUnitPrice, type WineVariant } from '../lib/extraSelection';
import { formatEuro } from './TouristTaxWidget';
import { intlLocale } from '../lib/locale';
import { useStatusBarOverlay } from '../lib/statusBar';

/** Il rosso vino della scheda e della card in evidenza. */
export const WINE_RED = '#7a1f2b';
/** L'arancio dello spritz: bottone, accenti e barra di stato. */
export const SPRITZ_ORANGE = '#c2410c';
/** L'arancio decorativo dello spritz (filetto sopra il nome, bordo della frase). */
export const SPRITZ_DECOR = '#e8590c';
/** L'oro del prosecco: bottone, accenti e barra di stato (bianco sopra: 5,6:1). */
export const PROSECCO_GOLD = '#846214';
/** Il verde oliva del vino bianco: bottone, accenti e barra di stato (bianco sopra: 5,9:1). */
export const WHITE_OLIVE = '#5c6b1f';
/** Il verde-paglierino decorativo del bianco (filetto sopra il nome, bordo della frase). */
export const WHITE_DECOR = '#B5BF5A';
/** L'oro decorativo del prosecco (filetto sopra il nome, bordo della frase). */
export const PROSECCO_DECOR = '#C9A227';
/** Quello che il Tab può raggiungere dentro la scheda (come gli altri fogli dell'app). */
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** "Riduci movimento" del telefono (come PlacesSwitch ed ExploreSectionNav). */
function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}
/** Serif di sistema: niente font esterni da scaricare. */
export const WINE_SERIF: React.CSSProperties = { fontFamily: 'Georgia, "Times New Roman", Times, serif' };

// ------------------------------------------------------------------
// Vino, spritz o prosecco: colori ed etichette
// ------------------------------------------------------------------

export type WineSheetKind = 'wine' | 'spritz' | 'prosecco' | 'white';

/**
 * Che scheda apre questa bottiglia: lo decide il channel manager, di serie è
 * il vino (anche per un tipo che questa versione dell'app non conosce).
 */
export const wineSheetKind = (v: WineVariant | null | undefined): WineSheetKind => {
  const kind = v && v.info ? v.info.kind : undefined;
  return kind === 'spritz' || kind === 'prosecco' || kind === 'white' ? kind : 'wine';
};

interface SheetTheme {
  /** Colore del bottone e della barra di stato. */
  accent: string;
  /** "Scopri il vino" / "Scopri lo spritz" / "Scopri il prosecco". */
  discoverKey: string;
  /** Classi del link "Scopri..." nelle righe e nella card. */
  discoverText: string;
  kickerKey: string;
  /** L'occhiello dice anche la denominazione (solo il vino: spritz e prosecco no). */
  kickerAppellation: boolean;
  /** La riga sotto il nome ("Bottiglia da 75 cl, già miscelato"); assente = nessuna. */
  subtitleKey?: string;
  storyKey: string;
  tastingKey: string;
  pairingsKey: string;
  /** Le etichette delle caselle "Bicchieri" e "A bicchiere" ("Calici" per il prosecco). */
  servingsKey: string;
  perGlassKey: string;
  /** Il filetto sopra l'occhiello; assente = nessun filetto (il vino). */
  decor?: string;
  /** L'icona della pillola sulla foto. */
  badgeIcon: LucideIcon;
  kicker: string;
  taglineBorder: string;
  button: string;
  tile: string;
  /**
   * L'etichetta delle caselle ("FORMATO", "GRADAZIONE"...): sul fondo della
   * casella deve reggere 4,5:1 (testo di 11px).
   */
  tileLabel: string;
  /** Il riquadro dei passi di "Come servirlo". */
  stepsCard: string;
  step: string;
  pairIcon: string;
  heroBg: string;
  heroIcon: string;
  badgeText: string;
  /** Un tono solo per i profumi (spritz); assente = toni a giro (vino). */
  chip?: string;
  /** I dati "da etichetta" nell'ordine in cui si leggono. */
  factOrder: FactKey[];
  /** Formato in cl ("75 cl") invece che in ml ("750 ml"). */
  centilitres: boolean;
}

type FactKey = 'grape' | 'vintage' | 'abv' | 'aging' | 'size' | 'serving' | 'servings' | 'perGlass' | 'brand';

// Classi scritte per intero: Tailwind le trova solo così.
export const SHEET_THEMES: Record<WineSheetKind, SheetTheme> = {
  wine: {
    accent: WINE_RED,
    discoverKey: 'extras.wine.discover',
    discoverText: 'text-[#7a1f2b] dark:text-rose-300',
    kickerKey: 'extras.wine.kicker',
    kickerAppellation: true,
    storyKey: 'extras.wine.story',
    tastingKey: 'extras.wine.tasting',
    pairingsKey: 'extras.wine.pairings',
    servingsKey: 'extras.wine.servings',
    perGlassKey: 'extras.wine.perGlass',
    badgeIcon: MapPin,
    kicker: 'text-[#7a1f2b] dark:text-rose-300',
    taglineBorder: 'border-[#7a1f2b] dark:border-rose-400',
    button: 'bg-[#7a1f2b] shadow-[#7a1f2b]/25 focus-visible:ring-[#7a1f2b]',
    tile: 'bg-[#faf5ef] dark:bg-gray-800',
    tileLabel: 'text-gray-500 dark:text-gray-400',
    stepsCard: 'border-gray-100 dark:border-gray-800',
    step: 'bg-[#7a1f2b] dark:bg-rose-700',
    pairIcon: 'bg-[#fdf2f4] dark:bg-rose-900/30 text-[#7a1f2b] dark:text-rose-300',
    heroBg: 'bg-gradient-to-br from-rose-100 to-red-200 dark:from-rose-900/40 dark:to-red-900/30',
    heroIcon: 'text-rose-600 dark:text-rose-300',
    badgeText: 'text-gray-900',
    factOrder: ['grape', 'vintage', 'abv', 'aging', 'size', 'serving', 'servings', 'perGlass', 'brand'],
    centilitres: false,
  },
  spritz: {
    accent: SPRITZ_ORANGE,
    discoverKey: 'extras.spritz.discover',
    discoverText: 'text-[#c2410c] dark:text-orange-300',
    kickerKey: 'extras.spritz.kicker',
    kickerAppellation: false,
    subtitleKey: 'extras.spritz.subtitle',
    storyKey: 'extras.spritz.story',
    tastingKey: 'extras.spritz.tasting',
    pairingsKey: 'extras.spritz.pairings',
    servingsKey: 'extras.wine.servings',
    perGlassKey: 'extras.wine.perGlass',
    decor: SPRITZ_DECOR,
    badgeIcon: Sunset,
    kicker: 'text-[#c2410c] dark:text-orange-300',
    taglineBorder: 'border-[#e8590c] dark:border-orange-400',
    button: 'bg-[#c2410c] shadow-[#c2410c]/25 focus-visible:ring-[#c2410c]',
    tile: 'bg-[#fff4ea] border border-[#f3dcc7] dark:bg-gray-800 dark:border-gray-700',
    tileLabel: 'text-gray-500 dark:text-gray-400',
    stepsCard: 'border-gray-100 dark:border-gray-800',
    step: 'bg-[#c2410c]',
    pairIcon: 'bg-orange-100 dark:bg-orange-900/30 text-[#c2410c] dark:text-orange-300',
    heroBg: 'bg-gradient-to-br from-orange-100 to-orange-300 dark:from-orange-900/40 dark:to-orange-800/30',
    heroIcon: 'text-orange-600 dark:text-orange-300',
    badgeText: 'text-orange-800',
    chip: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200',
    factOrder: ['size', 'abv', 'serving', 'servings', 'perGlass', 'brand', 'grape', 'vintage', 'aging'],
    centilitres: true,
  },
  // Il prosecco: la scheda dello spritz, con l'oro chiaro delle bollicine.
  // "Cos'è", "Il gusto" e "Quando e con cosa" sono gli stessi titoli dello
  // spritz (extras.spritz.*); "Calici" e "A calice" sono suoi.
  prosecco: {
    accent: PROSECCO_GOLD,
    discoverKey: 'extras.prosecco.discover',
    discoverText: 'text-[#846214] dark:text-amber-300',
    kickerKey: 'extras.prosecco.kicker',
    kickerAppellation: false,
    subtitleKey: 'extras.prosecco.subtitle',
    storyKey: 'extras.spritz.story',
    tastingKey: 'extras.spritz.tasting',
    pairingsKey: 'extras.spritz.pairings',
    servingsKey: 'extras.prosecco.servings',
    perGlassKey: 'extras.prosecco.perGlass',
    decor: PROSECCO_DECOR,
    badgeIcon: Sparkles,
    kicker: 'text-[#846214] dark:text-amber-300',
    taglineBorder: 'border-[#C9A227] dark:border-amber-400',
    button: 'bg-[#846214] shadow-[#846214]/25 focus-visible:ring-[#846214]',
    tile: 'bg-[#FBF4E0] border border-[#EEDFB8] dark:bg-gray-800 dark:border-gray-700',
    // Il grigio caldo del mockup: 5,8:1 sull'oro chiaro (il gray-500 faceva 4,4:1).
    tileLabel: 'text-[#6b5d52] dark:text-gray-400',
    stepsCard: 'bg-[#FFFDF7] border-[#EDE0BE] dark:bg-transparent dark:border-gray-800',
    step: 'bg-[#846214]',
    pairIcon: 'bg-[#F6EBCB] dark:bg-amber-900/30 text-[#846214] dark:text-amber-300',
    heroBg: 'bg-gradient-to-br from-[#FBF0D6] to-[#EFD9A0] dark:from-amber-900/40 dark:to-yellow-900/30',
    heroIcon: 'text-[#846214] dark:text-amber-300',
    badgeText: 'text-[#6F5412]',
    chip: 'bg-[#F6EBCB] text-[#6F5412] dark:bg-amber-900/30 dark:text-amber-200',
    factOrder: ['size', 'abv', 'serving', 'servings', 'perGlass', 'brand', 'grape', 'vintage', 'aging'],
    centilitres: true,
  },
  // Il vino bianco generico (marca che puo' variare): la scheda del prosecco
  // nel verde-paglierino del bianco nel bicchiere. "Cos'è", "Il gusto" e
  // "Quando e con cosa" come spritz e prosecco; "Bicchieri" come il vino.
  white: {
    accent: WHITE_OLIVE,
    discoverKey: 'extras.white.discover',
    discoverText: 'text-[#5C6B1F] dark:text-lime-300',
    kickerKey: 'extras.white.kicker',
    kickerAppellation: false,
    subtitleKey: 'extras.white.subtitle',
    storyKey: 'extras.spritz.story',
    tastingKey: 'extras.spritz.tasting',
    pairingsKey: 'extras.spritz.pairings',
    servingsKey: 'extras.wine.servings',
    perGlassKey: 'extras.wine.perGlass',
    decor: WHITE_DECOR,
    badgeIcon: Grape,
    kicker: 'text-[#5C6B1F] dark:text-lime-300',
    taglineBorder: 'border-[#B5BF5A] dark:border-lime-400',
    button: 'bg-[#5C6B1F] shadow-[#5C6B1F]/25 focus-visible:ring-[#5C6B1F]',
    tile: 'bg-[#F6F7E6] border border-[#E3E6C2] dark:bg-gray-800 dark:border-gray-700',
    // Grigio caldo: 6,4:1 sul paglierino delle caselle.
    tileLabel: 'text-[#5f5a48] dark:text-gray-400',
    stepsCard: 'bg-[#FEFEF9] border-[#E8EACF] dark:bg-transparent dark:border-gray-800',
    step: 'bg-[#5C6B1F]',
    pairIcon: 'bg-[#EEF1D2] dark:bg-lime-900/30 text-[#5C6B1F] dark:text-lime-300',
    heroBg: 'bg-gradient-to-br from-[#F4F6DF] to-[#DCE4A6] dark:from-lime-900/40 dark:to-green-900/30',
    heroIcon: 'text-[#5C6B1F] dark:text-lime-300',
    badgeText: 'text-[#4A5618]',
    chip: 'bg-[#EEF1D2] text-[#4A5618] dark:bg-lime-900/30 dark:text-lime-200',
    factOrder: ['size', 'abv', 'serving', 'servings', 'perGlass', 'brand', 'grape', 'vintage', 'aging'],
    centilitres: true,
  },
};

/** Tema della scheda per questa bottiglia. */
export const wineSheetTheme = (v: WineVariant | null | undefined): SheetTheme => SHEET_THEMES[wineSheetKind(v)];

// ------------------------------------------------------------------
// Aiuti condivisi con la card della bottiglia (ExtraServiceForm)
// ------------------------------------------------------------------

/** C'è davvero qualcosa da raccontare? Un `info` vuoto non apre la scheda. */
export const wineHasInfo = (v: WineVariant): boolean => {
  const i = v.info;
  if (!i) return false;
  const values: unknown[] = [
    i.producer, i.appellation, i.grape, i.vintage, i.abv, i.aging, i.sizeMl, i.servingTemp,
    i.badge, i.tagline, i.story, i.heroImageUrl, i.abvMin, i.servings, i.servingMl, i.serveNote,
  ];
  return values.some((x) => x !== undefined && x !== null && x !== '')
    || (i.notes || []).length > 0
    || (i.pairings || []).length > 0
    || (i.serveSteps || []).length > 0;
};

/**
 * "Kōra · Primitivo del Salento" → nome grande "Kōra" e sotto "Primitivo del
 * Salento". Senza "·" il nome resta intero.
 */
export const splitWineName = (name: string): { head: string; rest: string } => {
  const at = name.indexOf('·');
  if (at > 0) {
    const head = name.slice(0, at).trim();
    const rest = name.slice(at + 1).trim();
    if (head && rest) return { head, rest };
  }
  return { head: name, rest: '' };
};

/** La gradazione nella lingua dell'app: "14,5%" in italiano, "14.5%" in inglese. */
export const formatAbv = (abv: number, locale: string): string => {
  try {
    return new Intl.NumberFormat(intlLocale(locale), { style: 'percent', maximumFractionDigits: 1 }).format(abv / 100);
  } catch {
    return `${abv}%`;
  }
};

/** Un intervallo di gradazione: "8–11%" (in francese e tedesco "8–11 %"). */
export const formatAbvRange = (min: number, max: number, locale: string): string => {
  if (min === max) return formatAbv(max, locale);
  let low = String(min);
  try {
    low = new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: 1 }).format(min);
  } catch { /* numero così com'è */ }
  return `${low}–${formatAbv(max, locale)}`;
};

/** La gradazione della bottiglia: l'intervallo se c'è, altrimenti il valore secco. */
const abvText = (info: WineInfo, locale: string): string => {
  if (typeof info.abvMin === 'number' && typeof info.abvMax === 'number' && info.abvMin > 0 && info.abvMin <= info.abvMax) {
    return formatAbvRange(info.abvMin, info.abvMax, locale);
  }
  return typeof info.abv === 'number' && info.abv > 0 ? formatAbv(info.abv, locale) : '';
};

const formatSize = (ml: number, centilitres = false): string =>
  centilitres && Math.round(ml) % 10 === 0 ? `${Math.round(ml) / 10} cl` : `${Math.round(ml)} ml`;

/** La riga corta sotto il nome: "2022 · 14,5% · Legno di rovere" (solo i dati presenti). */
export const wineFactsLine = (info: WineInfo | undefined, locale: string): string => {
  if (!info) return '';
  const parts: string[] = [];
  if (info.vintage) parts.push(info.vintage);
  const abv = abvText(info, locale);
  if (abv) parts.push(abv);
  if (info.aging) parts.push(info.aging);
  return parts.join(' · ');
};

/**
 * Lo spazio tra due frasi dell'avviso: nessuno dopo un punto a larghezza
 * piena (cinese "。"), che lo spazio ce l'ha già dentro.
 */
export const sentenceGap = (sentence: string): string => (/[\u3002\uff01\uff1f]$/.test(sentence.trim()) ? '' : ' ');

// Toni caldi per i profumi: uno diverso per chip, a giro.
const NOTE_TONES = [
  'bg-rose-50 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200',
  'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
  'bg-orange-50 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200',
  'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200',
  'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
];

// ------------------------------------------------------------------
// La scheda
// ------------------------------------------------------------------

interface WineSheetProps {
  /** La bottiglia da raccontare; null = scheda chiusa. */
  wine: WineVariant | null;
  locale: string;
  /** La bottiglia si può ancora comprare? Se no il bottone è spento. */
  available: boolean;
  onClose: () => void;
  /** Aggiunge UNA bottiglia di questo vino: la stessa funzione del "+". */
  onAdd: () => void;
}

/** La foto grande: prima quella della scheda, poi quella della bottiglia, poi il segnaposto. */
const WineHero: React.FC<{ wine: WineVariant; theme: SheetTheme }> = ({ wine, theme }) => {
  const candidates = [guestAssetUrl(wine.info?.heroImageUrl), guestAssetUrl(wine.imageUrl)]
    .filter((u): u is string => !!u);
  const [failed, setFailed] = useState(0);
  useEffect(() => { setFailed(0); }, [wine.id]);
  const src = candidates[failed];
  const BadgeIcon = theme.badgeIcon;
  return (
    <div className={`relative w-full ${theme.heroBg}`} style={{ height: 440, maxHeight: '62vh' }}>
      {src ? (
        // Decorativa: il nome lo dicono già il dialogo e il titolo sotto (e
        // la foto del prosecco sono due flûte, non la bottiglia).
        <img
          key={src}
          src={src}
          alt=""
          onError={() => setFailed((n) => n + 1)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className={`absolute inset-0 flex items-center justify-center ${theme.heroIcon}`}>
          <Wine className="h-16 w-16" strokeWidth={1.25} />
        </div>
      )}
      {/* Un velo in alto: pillola e X restano leggibili su qualsiasi foto. */}
      <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/30 to-transparent pointer-events-none" />
      {wine.info?.badge && (
        <span
          className={`absolute left-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-[12px] font-bold shadow-sm ${theme.badgeText}`}
          // +10px nell'app installata su iPhone (stile nativo, lib/statusBar): fuori dalla sfumatura di iOS.
          style={{ top: 'calc(14px + env(safe-area-inset-top, 0px) + var(--nva-top-edge, 0px))' }}
        >
          <BadgeIcon className="h-3.5 w-3.5" style={{ color: theme.accent }} strokeWidth={2.5} />
          {wine.info.badge}
        </span>
      )}
    </div>
  );
};

const WineSheet: React.FC<WineSheetProps> = ({ wine, locale, available, onClose, onAdd }) => {
  const { t } = useTranslation();
  const open = !!wine;
  // La barra di stato col colore del bottone, in chiaro e in scuro (lib/statusBar).
  const accent = wineSheetTheme(wine).accent;
  useStatusBarOverlay(accent, open, { dark: accent });
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  // "Riduci movimento": la scheda compare in dissolvenza, senza salire.
  const reduceMotion = open && prefersReducedMotion();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Scroll della pagina bloccato sotto la scheda (come gli altri fogli).
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Esc chiude; il focus parte dalla X e alla chiusura torna a chi l'ha aperta.
  // Tab resta dentro la scheda (aria-modal): dall'ultimo si torna alla X, e
  // Maiusc+Tab dalla X va all'ultimo, come negli altri fogli dell'app.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      const dialog = dialogRef.current;
      if (e.key !== 'Tab' || !dialog) return;
      const focusables = Array.prototype.slice.call(dialog.querySelectorAll(FOCUSABLE)) as HTMLElement[];
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !dialog.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    if (closeRef.current) closeRef.current.focus({ preventScroll: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && typeof opener.focus === 'function') {
        try { opener.focus({ preventScroll: true }); } catch { /* elemento sparito */ }
      }
    };
  }, [open]);

  if (typeof document === 'undefined') return null;

  const renderBody = (w: WineVariant) => {
    const info: WineInfo = w.info || {};
    const theme = wineSheetTheme(w);
    // Il nome grande: quello della scheda se il channel manager lo manda
    // ("Prosecco"), altrimenti quello della bottiglia ("Bottiglia di Prosecco").
    const { head, rest } = splitWineName(info.title || w.name);
    const unit = wineUnitPrice(w);
    const discounted = unit < w.price;
    const size = typeof info.sizeMl === 'number' && info.sizeMl > 0 ? formatSize(info.sizeMl, theme.centilitres) : '';
    const abv = abvText(info, locale);
    const abvRange = typeof info.abvMin === 'number' && typeof info.abvMax === 'number' && abv !== '';
    const servings = typeof info.servings === 'number' && info.servings > 0 ? info.servings : 0;
    const servingMl = typeof info.servingMl === 'number' && info.servingMl > 0 ? info.servingMl : 0;

    const allFacts: Record<FactKey, { label: string; value: string } | null> = {
      grape: info.grape ? { label: t('extras.wine.grape'), value: info.grape } : null,
      vintage: info.vintage ? { label: t('extras.wine.vintage'), value: info.vintage } : null,
      abv: abv ? { label: t('extras.wine.abv'), value: `${abv} vol` } : null,
      aging: info.aging ? { label: t('extras.wine.aging'), value: info.aging } : null,
      size: size ? { label: t('extras.wine.size'), value: size } : null,
      serving: info.servingTemp ? { label: t('extras.wine.serving'), value: info.servingTemp } : null,
      servings: servings ? { label: t(theme.servingsKey), value: t('extras.wine.servingsValue', { n: servings }) } : null,
      perGlass: servingMl ? { label: t(theme.perGlassKey), value: t('extras.wine.perGlassValue', { ml: servingMl }) } : null,
      brand: info.brandMayVary ? { label: t('extras.wine.brand'), value: t('extras.wine.brandVaries') } : null,
    };
    const facts = theme.factOrder
      .map((k) => allFacts[k])
      .filter((f): f is { label: string; value: string } => !!f);
    const notes = (info.notes || []).filter(Boolean);
    const pairings = (info.pairings || []).filter(Boolean);
    const steps = (info.serveSteps || []).filter(Boolean);
    const kicker = theme.kickerAppellation && info.appellation ? `${t(theme.kickerKey)} · ${info.appellation}` : t(theme.kickerKey);
    const sizeLabel = size ? t('extras.wine.bottleSize', { size }) : head;
    const notice = !!info.adultsOnly || !!info.containsSulphites;
    const adultsOnlyText = t('extras.wine.adultsOnly');
    const sulphitesText = t('extras.wine.sulphites');

    return (
      <>
        {/* La X resta ferma in alto mentre il contenuto scorre. */}
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label={t('extras.wine.close')}
          className="absolute right-3 z-20 w-11 h-11 rounded-full bg-black/45 backdrop-blur-sm text-white flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          // +10px nell'app installata su iPhone (stile nativo, lib/statusBar): fuori dalla sfumatura di iOS.
          style={{ top: 'calc(10px + env(safe-area-inset-top, 0px) + var(--nva-top-edge, 0px))' }}
        >
          <X className="h-5 w-5" strokeWidth={2.5} />
        </button>

        <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
          <WineHero wine={w} theme={theme} />

          <div className="px-5 pt-5 pb-8 space-y-7">
            <div>
              {theme.decor && <span aria-hidden="true" className="block mb-2.5 w-10 h-0.5" style={{ background: theme.decor }} />}
              <p className={`text-[12px] font-bold uppercase tracking-[0.14em] ${theme.kicker}`}>{kicker}</p>
              <h2 className="mt-1.5 text-[34px] leading-[1.1] text-gray-900 dark:text-white" style={WINE_SERIF}>{head}</h2>
              {rest && <p className="mt-1 text-[18px] text-gray-700 dark:text-gray-200" style={WINE_SERIF}>{rest}</p>}
              {theme.subtitleKey && size && (
                <p className="mt-1.5 text-[17px] font-semibold text-gray-900 dark:text-white">{t(theme.subtitleKey, { size })}</p>
              )}
              {info.producer && <p className="mt-1.5 text-[14px] text-gray-500 dark:text-gray-400">{info.producer}</p>}
              {info.brandMayVary && (
                <p className="mt-1.5 flex items-center gap-1.5 text-[14px] text-gray-600 dark:text-gray-300">
                  <Info className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  {t('extras.wine.brandMayVary')}
                </p>
              )}
            </div>

            {info.tagline && (
              <p className={`text-[19px] leading-snug italic text-gray-800 dark:text-gray-100 border-l-[3px] pl-3.5 ${theme.taglineBorder}`} style={WINE_SERIF}>
                {info.tagline}
              </p>
            )}

            {facts.length > 0 && (
              <div>
                <div className="grid grid-cols-3 gap-2">
                  {facts.map((f) => (
                    <div key={f.label} className={`rounded-2xl px-2.5 py-3 min-w-0 ${theme.tile}`}>
                      {/* Parole lunghe ("TRINKTEMPERATUR"): vanno a capo dentro la casella
                          (sillabate dove la lingua lo permette), mai fuori dallo schermo. */}
                      <span className={`block text-[11px] font-semibold uppercase tracking-wide hyphens-auto [overflow-wrap:anywhere] ${theme.tileLabel}`}>{f.label}</span>
                      <span className="block mt-1 text-[14px] font-semibold text-gray-900 dark:text-white leading-snug break-words">{f.value}</span>
                    </div>
                  ))}
                </div>
                {abvRange && (
                  <p className="mt-2 text-[12.5px] leading-snug text-gray-500 dark:text-gray-400">{t('extras.wine.abvOnLabel')}</p>
                )}
              </div>
            )}

            {info.story ? (
              <section>
                <h3 className="text-[21px] text-gray-900 dark:text-white" style={WINE_SERIF}>{t(theme.storyKey)}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-line">{info.story}</p>
              </section>
            ) : w.description ? (
              <p className="text-[15px] leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-line">{w.description}</p>
            ) : null}

            {(steps.length > 0 || info.serveNote) && (
              <section>
                <h3 className="text-[21px] text-gray-900 dark:text-white" style={WINE_SERIF}>{t('extras.wine.serve')}</h3>
                {steps.length > 0 && (
                  <ol className={`mt-3 space-y-3 rounded-2xl border p-3.5 ${theme.stepsCard}`}>
                    {steps.map((s, idx) => (
                      <li key={`${s}-${idx}`} className="flex items-start gap-3">
                        <span
                          aria-hidden="true"
                          className={`w-7 h-7 rounded-full text-white text-[15px] font-bold flex items-center justify-center flex-shrink-0 ${theme.step}`}
                          style={WINE_SERIF}
                        >
                          {idx + 1}
                        </span>
                        <span className="pt-0.5 text-[15px] leading-snug text-gray-800 dark:text-gray-100">{s}</span>
                      </li>
                    ))}
                  </ol>
                )}
                {info.serveNote && (
                  <p className="mt-2 text-[12.5px] leading-snug text-gray-500 dark:text-gray-400">{info.serveNote}</p>
                )}
              </section>
            )}

            {notes.length > 0 && (
              <section>
                <h3 className="text-[21px] text-gray-900 dark:text-white" style={WINE_SERIF}>{t(theme.tastingKey)}</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {notes.map((n, idx) => (
                    <span key={`${n}-${idx}`} className={`px-3 py-1.5 rounded-full text-[14px] font-semibold ${theme.chip || NOTE_TONES[idx % NOTE_TONES.length]}`}>
                      {n}
                    </span>
                  ))}
                </div>
                {/* "I profumi tipici…": la marca può variare, i profumi non sono una promessa. */}
                {info.tastingNote && (
                  <p className="mt-2 text-[12.5px] leading-snug text-gray-500 dark:text-gray-400">{info.tastingNote}</p>
                )}
              </section>
            )}

            {pairings.length > 0 && (
              <section>
                <h3 className="text-[21px] text-gray-900 dark:text-white" style={WINE_SERIF}>{t(theme.pairingsKey)}</h3>
                <ul className="mt-2 divide-y divide-gray-100 dark:divide-gray-800">
                  {pairings.map((p, idx) => (
                    <li key={`${p}-${idx}`} className="flex items-center gap-3 py-2.5">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${theme.pairIcon}`}>
                        <UtensilsCrossed className="h-4 w-4" />
                      </span>
                      <span className="text-[15px] text-gray-800 dark:text-gray-100">{p}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Alcol, età, allergeni: solo se il channel manager lo chiede. */}
            {notice && (
              <div role="note" className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-900/20 px-3.5 py-3">
                {info.adultsOnly && (
                  <span
                    aria-hidden="true"
                    className="w-10 h-10 rounded-xl border-2 border-amber-700 text-amber-800 dark:border-amber-300 dark:text-amber-200 text-[15px] font-bold flex items-center justify-center flex-shrink-0"
                    style={WINE_SERIF}
                  >
                    18+
                  </span>
                )}
                <p className="text-[14px] leading-snug text-amber-900 dark:text-amber-100">
                  {info.adultsOnly && <strong>{adultsOnlyText}</strong>}
                  {info.adultsOnly && sentenceGap(adultsOnlyText)}
                  {info.containsSulphites && `${sulphitesText}${sentenceGap(sulphitesText)}`}
                  {t('extras.wine.labelDetails')}
                </p>
              </div>
            )}

            <p className="text-[14px] text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 rounded-2xl px-4 py-3 leading-relaxed">
              {t('extras.wine.delivery')}
            </p>
          </div>
        </div>

        {/* Barra fissa: formato, prezzo (quello vero, con lo sconto) e il bottone. */}
        <div
          className="flex-shrink-0 flex items-center gap-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 px-5 pt-3"
          style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="flex-shrink-0 leading-tight min-w-0">
            <span className="block text-[12px] font-semibold text-gray-500 dark:text-gray-400 truncate max-w-[9rem]">{sizeLabel}</span>
            <span className="inline-flex items-baseline gap-1.5 text-[22px] font-bold text-gray-900 dark:text-white tabular-nums">
              {discounted && <del className="text-[13px] font-medium text-gray-400 dark:text-gray-500">{formatEuro(w.price, locale)}</del>}
              {formatEuro(unit, locale)}
            </span>
          </div>
          <button
            type="button"
            disabled={!available}
            onClick={() => { onAdd(); onClose(); }}
            className={`flex-1 py-3.5 rounded-2xl text-white text-[15px] font-bold shadow-lg active:brightness-95 disabled:opacity-50 disabled:shadow-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${theme.button}`}
          >
            {available ? t('extras.wine.addToStay') : t('extras.wine.unavailable')}
          </button>
        </div>
      </>
    );
  };

  return ReactDOM.createPortal(
    <AnimatePresence>
      {wine && (
        // Sul telefono il foglio parte sotto la striscia delle prove (pannello,
        // lib/statusBar; di serie 0px): lì sotto c'è il velo, dello stesso
        // colore della striscia, e la foto resta intera.
        <div
          key="wine-sheet"
          ref={dialogRef}
          className="fixed inset-0 z-[9999] flex justify-center pt-[var(--nva-top-gap,0px)] sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label={wine.name}
          // I testi della scheda sono nella lingua dell'app: sillabazione
          // (hyphens) e lettori di schermo la leggono da qui.
          lang={String(locale || '').split('-')[0] || undefined}
        >
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="relative w-full h-full sm:h-[92vh] sm:max-w-md sm:rounded-3xl overflow-hidden bg-white dark:bg-gray-900 shadow-2xl flex flex-col"
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 32 }}
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 32 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
          >
            {renderBody(wine)}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default WineSheet;
